import React from 'react';
import PropTypes from 'prop-types';
import { wrapFunctionalComponent } from 'utils/framework';
import Select from 'components/Inputs/Select/Select';
import localeUtils from 'utils/LanguageLocale';
import AddressFormEN from 'components/Addresses/AddressForm/locales/AddressForm_en_US';
import AddressFormFR from 'components/Addresses/AddressForm/locales/AddressForm_fr_CA';

// GIM-5453: source the dropdown's label from the AddressForm locale resource so it localizes
// (fr-CA) instead of hardcoding English. Callers may still override via the `label` prop.
const getText = localeUtils.getLocalisedDataFromResource(AddressFormEN, AddressFormFR);

/**
 * AVS Loqate v2 (PRD §3.2, GIM-5453): when a ZIP/postal code maps to more than one city, the shopper
 * MUST choose one — auto-filling the wrong city produces an undeliverable address. The City field's
 * own slot becomes this dropdown, rendered with the shared <Select> so it matches the State/Region
 * dropdown beside it (Figma single-slot layout). Nothing is pre-selected, so the shopper must pick;
 * a single-city ZIP never reaches this component (the caller auto-fills + locks the City text input).
 */
function MultiCityZipDropdown({ cities = [], onSelectCity, label }) {
    if (!Array.isArray(cities) || cities.length < 2) {
        return null;
    }

    const handleChange = e => {
        const index = e.target.value;

        if (index === '') {
            return;
        }

        const selectedCity = cities[Number(index)];

        // Guard a stale/out-of-range index (e.g. rapid ZIP changes) so onSelectCity is never called
        // with undefined — the parent destructures { city, state } and would throw.
        if (!selectedCity) {
            return;
        }

        onSelectCity(selectedCity);
    };

    return (
        <Select
            name='city'
            label={label || getText('city')}
            required={true}
            hideAsterisk={localeUtils.isUS()}
            value=''
            onChange={handleChange}
            data-at={Sephora.debug.dataAt('multi_city_select')}
        >
            {cities.map((option, index) => (
                <option
                    key={`${option.city}-${option.state}-${index}`}
                    value={index}
                >
                    {option.city}
                </option>
            ))}
        </Select>
    );
}

MultiCityZipDropdown.propTypes = {
    cities: PropTypes.arrayOf(
        PropTypes.shape({
            city: PropTypes.string,
            state: PropTypes.string
        })
    ),
    onSelectCity: PropTypes.func.isRequired,
    label: PropTypes.string
};

export default wrapFunctionalComponent(MultiCityZipDropdown, 'MultiCityZipDropdown');
